'use client';

import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import type { ComponentType } from 'react';

// ─── Typy ─────────────────────────────────────────────────────────────────────

interface PublicTicket {
  ticketNumber: string;
  status: string;
  categoryName: string | null;
  addressText: string | null;
  lat: number | null;
  lng: number | null;
  createdAt: string;
}

// ─── Stałe ────────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  NOWE: 'Nowe',
  WERYFIKACJA: 'Weryfikacja',
  W_REALIZACJI: 'W realizacji',
  OCZEKUJE_ZEWN: 'Oczekuje',
  ZAMKNIETE: 'Zamknięte',
  ODRZUCONE: 'Odrzucone',
  PRZEKAZANE: 'Przekazane',
};

const STATUS_COLOR: Record<string, { bg: string; text: string; dot: string }> = {
  NOWE:          { bg: '#FFFBEB', text: '#D97706', dot: '#F59E0B' },
  WERYFIKACJA:   { bg: '#EFF6FF', text: '#2563EB', dot: '#3B82F6' },
  W_REALIZACJI:  { bg: '#F5F3FF', text: '#7C3AED', dot: '#8B5CF6' },
  OCZEKUJE_ZEWN: { bg: '#FFF7ED', text: '#EA580C', dot: '#F97316' },
  ZAMKNIETE:     { bg: '#ECFDF5', text: '#059669', dot: '#10B981' },
  ODRZUCONE:     { bg: '#FEF2F2', text: '#DC2626', dot: '#EF4444' },
  PRZEKAZANE:    { bg: '#F0FDF4', text: '#16A34A', dot: '#22C55E' },
};

// ─── Mapa z pinezkami (ładowana dynamicznie) ──────────────────────────────────

const PublicMap = dynamic(() => import('./PublicMap'), {
  ssr: false,
  loading: () => (
    <div className="w-full h-full bg-sky-50 flex items-center justify-center rounded-2xl">
      <div className="w-8 h-8 border-4 border-sky-400 border-t-transparent rounded-full animate-spin" />
    </div>
  ),
}) as ComponentType<{ tickets: PublicTicket[] }>;

// ─── Komponent wyszukiwarki statusu ──────────────────────────────────────────

function StatusSearch() {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const num = value.trim().toUpperCase();
    if (!num) return;
    setLoading(true);
    setError('');
    try {
      const apiBase = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:3001';
      const res = await fetch(`${apiBase}/public/tickets/${num}/status`, {
        headers: { Host: window.location.hostname },
      });
      if (!res.ok) {
        setError('Nie znaleziono zgłoszenia o podanym numerze.');
        setLoading(false);
        return;
      }
      window.location.href = `/status/${num}`;
    } catch {
      setError('Błąd połączenia — spróbuj ponownie.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSearch} className="w-full">
      <div className="flex gap-2">
        <input
          type="text"
          value={value}
          onChange={(e) => { setValue(e.target.value.toUpperCase()); setError(''); }}
          placeholder="np. SZP-2024-00123"
          maxLength={20}
          className="flex-1 px-4 py-3 text-sm border-2 border-gray-200 bg-white rounded-xl outline-none focus:border-sky-400 font-mono placeholder-gray-400 text-gray-800"
        />
        <button
          type="submit"
          disabled={loading || !value.trim()}
          className="px-5 py-3 bg-sky-500 text-white rounded-xl text-sm font-bold hover:bg-sky-600 disabled:opacity-50 transition shadow whitespace-nowrap"
        >
          {loading ? '...' : 'Sprawdź'}
        </button>
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5">{error}</p>
      )}
    </form>
  );
}

// ─── Strona główna /zgloszenia ────────────────────────────────────────────────

export default function ZgloszeniaPage() {
  const [tickets, setTickets] = useState<PublicTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [tenantName, setTenantName] = useState('gminie');

  const apiBase = process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:3001';

  useEffect(() => {
    const host = window.location.hostname;
    fetch(`${apiBase}/public/tickets/recent?limit=50`, { headers: { Host: host } })
      .then((r) => r.ok ? r.json() : [])
      .catch(() => [])
      .then((t) => { setTickets(Array.isArray(t) ? t : []); })
      .finally(() => setLoading(false));
    fetch(`${apiBase}/public/branding`, { headers: { Host: host } })
      .then((r) => r.ok ? r.json() : null)
      .then((b) => { if (b?.displayName) setTenantName(b.displayName); })
      .catch(() => {});
  }, [apiBase]);

  const mapTickets = tickets.filter((t) => t.lat && t.lng);

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">

      {/* ── HEADER ───────────────────────────────────────────────────────────── */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-30 shadow-sm">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-4">
          <div className="flex flex-col items-start gap-0.5">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/app-logo.png" alt="HaloGmina.pl" className="h-32 w-auto" />
            <p className="text-[11px] text-gray-400 leading-tight">Twoja gmina na wyciągnięcie ręki</p>
          </div>
          <nav className="flex items-center gap-2">
            <Link href="/zgloszenia"
              className="text-sm font-medium text-sky-600 px-3 py-1.5 rounded-lg bg-sky-50 hidden sm:block">
              Zgłoszenia
            </Link>
            <Link href="/report"
              className="text-sm font-semibold text-white bg-sky-500 hover:bg-sky-600 px-4 py-2 rounded-xl transition shadow-sm">
              + Nowe zgłoszenie
            </Link>
          </nav>
        </div>
      </header>

      {/* ── HERO ─────────────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-sky-500 via-sky-600 to-blue-700 text-white px-4 py-12 sm:py-16">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1 rounded-full text-xs font-medium mb-4">
            <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
            System aktywny — przyjmujemy zgłoszenia
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold mb-3 leading-tight">
            Zgłoś problem w {tenantName}
          </h1>
          <p className="text-sky-100 text-sm sm:text-base mb-8 max-w-xl mx-auto">
            Dziury w jezdni, uszkodzone chodniki, awarie oświetlenia — zgłoś to szybko i śledź postęp naprawy.
          </p>

          <Link href="/report"
            className="inline-flex items-center gap-2 bg-white text-sky-600 font-bold px-7 py-3.5 rounded-2xl hover:bg-sky-50 transition shadow-lg text-sm sm:text-base">
            📤 Zgłoś nowy problem
          </Link>
        </div>
      </section>

      <main className="max-w-5xl mx-auto w-full px-4 py-8 flex-1">

        {/* ── MAPA ─────────────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden mb-8">
          {/* Legenda statusów */}
          <div className="px-4 pt-4 pb-2 flex items-center flex-wrap gap-3">
            <h2 className="font-bold text-gray-800 text-sm mr-2">🗺 Mapa zgłoszeń</h2>
            {Object.entries(STATUS_COLOR).filter(([s]) => !['ODRZUCONE'].includes(s)).map(([status, sc]) => (
              <span key={status} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full"
                style={{ background: sc.bg, color: sc.text }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: sc.dot }} />
                {STATUS_LABELS[status]}
              </span>
            ))}
          </div>
          <div className="h-[520px] sm:h-[580px]">
            {!loading && <PublicMap tickets={mapTickets} />}
            {loading && (
              <div className="h-full bg-sky-50 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-sky-400 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
        </div>

        {/* ── JAK TO DZIAŁA ─────────────────────────────────────────────────── */}
        <section className="mt-10">
          <h2 className="text-lg font-bold text-gray-800 mb-5 text-center">Jak to działa?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                step: 1,
                icon: '📍',
                title: 'Zgłoś problem',
                desc: 'Opisz usterke, zaznacz lokalizację na mapie i dodaj zdjęcia. Zajmie Ci to mniej niż 2 minuty.',
              },
              {
                step: 2,
                icon: '🔧',
                title: 'Urząd działa',
                desc: 'Twoje zgłoszenie trafia do odpowiedniego działu. Możesz śledzić postęp na bieżąco.',
              },
              {
                step: 3,
                icon: '✅',
                title: 'Problem rozwiązany',
                desc: 'Otrzymasz powiadomienie e-mail gdy status zgłoszenia się zmieni i problem zostanie naprawiony.',
              },
            ].map(({ step, icon, title, desc }) => (
              <div key={step} className="bg-white rounded-2xl border border-gray-200 p-5 text-center shadow-sm">
                <div className="w-10 h-10 bg-sky-50 border border-sky-100 rounded-full flex items-center justify-center text-xl mx-auto mb-3">
                  {icon}
                </div>
                <p className="text-xs font-bold text-sky-500 mb-1">KROK {step}</p>
                <h3 className="font-bold text-gray-800 mb-2">{title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── SPRAWDŹ STATUS ───────────────────────────────────────────────── */}
        <section className="mt-8 bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <p className="text-sm font-semibold text-gray-700 mb-3">Masz numer zgłoszenia? Sprawdź jego status:</p>
          <StatusSearch />
        </section>

        {/* ── CTA BOTTOM ───────────────────────────────────────────────────── */}
        <section className="mt-8 bg-gradient-to-r from-sky-500 to-blue-600 rounded-2xl p-6 sm:p-8 text-white text-center shadow-lg">
          <h2 className="text-xl sm:text-2xl font-extrabold mb-2">Widzisz problem w gminie?</h2>
          <p className="text-sky-100 text-sm mb-5">Nie czekaj — zgłoś to teraz. To zajmie tylko chwilę.</p>
          <Link href="/report"
            className="inline-flex items-center gap-2 bg-white text-sky-600 font-bold px-7 py-3.5 rounded-2xl hover:bg-sky-50 transition shadow-md text-sm">
            📤 Zgłoś problem teraz
          </Link>
        </section>
      </main>

      {/* ── FOOTER ───────────────────────────────────────────────────────────── */}
      <footer className="mt-8 bg-white border-t border-gray-100 py-6 px-4">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-400">
          <div className="flex items-center gap-2">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/app-logo.png" alt="HaloGmina.pl" className="h-[72px] w-auto" />
            <span>HaloGmina.pl © {new Date().getFullYear()}</span>
          </div>
          <div className="flex gap-4">
            <Link href="/report" className="hover:text-sky-500 transition">Nowe zgłoszenie</Link>
            <span className="text-gray-200">|</span>
            <Link href="/panel" className="hover:text-sky-500 transition">Panel urzędnika</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
